/**
 * PR-V0b — pointer / wheel / keyboard handlers for `Scene3DStage`.
 *
 * Lives outside the R3F `<Canvas>` so the same listeners drive cursor
 * crosshair tracking, Dynamic Input live-coord emission, MMB pan,
 * drag-rectangle rubber band, and wheel zoom-around-cursor — all sharing the
 * `clientX/Y → world` projection sibling DOM overlays use via `cameraSnapshot`.
 *
 * AutoCAD parity:
 *   - Wheel: pixel-locked zoom around cursor; `ZOOMFACTOR` per-notch ratio
 *     `1 + ZF/100` (ZF=60 → 1.6× in / 0.625× out).
 *   - MMB drag: pan; `(prevClient - currClient) / pixelsPerWorld` (CAD Y-up
 *     inverts dy).
 *   - LMB drag-rectangle: window-vs-crossing by sign of
 *     `currentClient.x - dragStart.x` (DDSELECT default).
 *   - Right-click: `RIGHTCLICKENTER` — empty-Enter submits the draft.
 *   - Esc: cancels rubber band + zoom-window/object picking (grip-drag cancel
 *     is delegated to the grips overlay, PR-G).
 */

import * as THREE from "three";

import {
  viewportStoredViewCenterFromWcs,
  viewportWcsViewCenter,
} from "@dxf-ai-editor/drawing-model";

import type { ViewportLike } from "./viewports/viewportTransformAdapter";
import { resolveActiveInputSpace } from "./viewports/activeInputSpace";
// cmd-pan P2-3 — canonical ortho→pixels-per-world conversion (same helper the
// R3F cameraApiBinder uses; pure THREE math, no React pulled in).
import { pixelsPerWorldFromOrtho } from "./orthoZoomConversion";
// cmd-pan P2-1 — focus gate so PAN-exit never swallows keys meant for an input/dialog.
import { isPanExitKeyGuarded } from "./panExitFocusGuard";
import {
  zoomViewportAroundCursor,
  panViewportByPixels,
} from "./viewports/viewportZoomPan";
// B12 — selection-cycling state (cursor badge / Shift+Space cycle).
import { useSelectionCyclingStore } from "../state/selectionCyclingState";
// MC4 S3 — the per-move OSNAP + hover hit-test logic now lives in a pure,
// THREE-free, in-VP-aware step. `OsnapBindings` is re-exported below so the
// existing import paths (StageInteractionsBinder, useOsnapBindings) keep
// working unchanged (dep-graph: barrel re-export, no caller churn).
import { type OsnapBindings } from "./snap/pointerSnapStep";
// W2.5 — the per-move OSNAP/hover resolution pipeline, its snap cache, and
// the LMB click-commit / pointerleave teardown now live in the dedicated
// pick-loop module (first slice to establish this seam; a later slice,
// OTRACK-SUBSYSTEM, extends it). Re-exported nowhere — `PickLoopHandle` is
// an internal implementation detail of `installStageInteractions`.
import { createInteractivePickLoop } from "./pickLoop/interactivePickLoop";
import {
  createTouchState,
  decideTwoFingerMode,
  distance as touchDistance,
  isDoubleTap,
  midpoint,
  pinchZoomFactor,
  LONG_PRESS_MS,
  TAP_MOVE_THRESHOLD_PX,
  type TouchState,
} from "./touchGestures";

/**
 * Re-export the OSNAP-binding contract from the pure `snap/pointerSnapStep`
 * module (MC4 S3 extraction). `OsnapBindings` moved there so the pure snap step
 * owns its own contract; re-exporting here keeps the existing import paths
 * (`scene/StageInteractionsBinder.ts`, `scene/useOsnapBindings.ts`) valid with
 * no caller churn (dep-graph: barrel re-export, one-directional
 * `stageInteractions → snap/pointerSnapStep` edge).
 */
export type { OsnapBindings };

/**
 * W2.5 — the interaction-binding contract types (`ViewportRedirectContext`,
 * `StageInteractionBindings`, `StageInteractionsHandle`) now live in the leaf
 * module `stageInteractionTypes.ts` so `pickLoop/interactivePickLoop.ts`
 * (which needs `StageInteractionBindings`) can import them WITHOUT a cycle
 * back through this file (this file imports the pick-loop factory FROM
 * `./pickLoop/interactivePickLoop` to install it — a two-way edge would be
 * a real, gate-failing import cycle even though the pick-loop's own import
 * is type-only). Re-exported verbatim so every existing
 * `from "./stageInteractions"` / `from "../stageInteractions"` import path
 * is UNCHANGED (dep-graph: barrel re-export, no caller churn).
 */
export type {
  ViewportRedirectContext,
  StageInteractionBindings,
  StageInteractionsHandle,
} from "./stageInteractionTypes";
import type {
  StageInteractionBindings,
  StageInteractionsHandle,
} from "./stageInteractionTypes";

// cmd-pan r5 — the zoom/pan constants + pure camera-math helpers moved to a
// sibling module (`stageCameraMath.ts`) to keep THIS file under the 2000-line
// cap when r5 added the P2-3 tween-cancel seam. Re-exported verbatim so every
// existing `from "../stageInteractions"` import path is unchanged (the project's
// standard split-into-sibling + barrel re-export pattern, no behaviour change).
export {
  DRAG_THRESHOLD_PX,
  ZOOMFACTOR_DEFAULT,
  WHEEL_NOTCH_DELTA,
  WHEEL_RATE,
  MIN_ZOOM,
  MAX_ZOOM,
  PICK_APERTURE_PX,
  clientToWorld,
  worldToClient,
  orthoPixelToWorld,
  pickApertureWorldTolerance,
  wheelDeltaToZoomFactor,
  zoomAroundCursor,
  panByPixels,
} from "./stageCameraMath";
import {
  DRAG_THRESHOLD_PX,
  MIN_ZOOM,
  clientToWorld,
  wheelDeltaToZoomFactor,
  zoomAroundCursor,
  panByPixels,
} from "./stageCameraMath";

/** Internal pan-drag tracker (one MMB drag at a time). */
interface PanDragState {
  startX: number;
  startY: number;
  camStartX: number;
  camStartY: number;
  /**
   * MC4-interactive Slice 3 — when set, this MMB pan is REDIRECTED into the
   * active VP's model view instead of the paper camera. Captured at
   * pointerdown so the whole drag commits to the VP it started over (matches
   * the main-camera pan, which captures camStartX/Y once). Holds the VP id,
   * the VP's starting view centre, viewHeight, target, twist, and on-screen
   * pixel height so each move recomputes the new viewCenter from the TOTAL
   * pixel delta (drift-free).
   *
   * FRAME: `startWcsViewCenter` is the **WCS** window centre
   * (`viewportWcsViewCenter`), and the result of `panViewportByPixels` —
   * `startWcsViewCenter − R(+twist)·pixel delta`, i.e. a WCS point, since that
   * `R(+twist)` is precisely the screen→WCS axis map `paperToModel` uses — is
   * converted back with `viewportStoredViewCenterFromWcs` before it reaches
   * the DCS `viewCenter` field. Exactly the wheel-zoom's discipline.
   *
   * This path USED to hold the stored DCS centre and write the rotated delta
   * straight back, on the (then-true) argument that a delta is frame-free.
   * Once the twist entered the DCS↔WCS map that argument died: the two frames'
   * AXES differ, `Δstored = R(−twist)·Δwcs`, and writing the WCS delta into
   * the DCS slot double-rotated every twisted-viewport pan (@codex PR #794 r4
   * P2). `viewTarget` is captured alongside so the inverse can be applied from
   * this snapshot without re-resolving the entity mid-gesture.
   */
  vpRedirect: {
    viewportId: string;
    startWcsViewCenter: { x: number; y: number };
    viewHeight: number;
    viewTarget: { x: number; y: number } | undefined;
    viewTwistAngle: number;
    vpScreenHeightPx: number;
  } | null;
}

/** Internal LMB drag tracker (rubber-band selection). */
interface LeftDragState {
  startClientX: number;
  startClientY: number;
}

/**
 * Install pointer/wheel/keyboard listeners. Returns a teardown
 * function that removes every listener.
 *
 * The function is intentionally framework-agnostic (no React
 * imports) — the React side just calls it from a `useEffect`.
 */
export function installStageInteractions(
  bindings: StageInteractionBindings,
): StageInteractionsHandle {
  const {
    canvas,
    overlayContainer,
    cameraRef,
    sizeRef,
    invalidate,
    setOverlayState,
    onCursorClientChange,
    onRightClickEnter,
    onContextMenu: onContextMenuCb,
    spatialIndexRef,
    refinedEntityPickRef,
    zoomPickingRef,
    onTouchZoomExtents,
    onTouchEscape,
    viewportRedirectRef,
    onViewportSetView,
    onPanComplete,
    onPanSnapshotView,
    onCancelPendingTween,
    panSessionActiveRef,
    onPanSessionEnd,
  } = bindings;

  // W2.5 — the interactive pick-loop (per-move OSNAP/hover resolution, snap
  // cache, LMB click-commit incl. the OSNAP-OVERRIDE-MODES-S2 extended-
  // override capture/clear, and `onPointerLeave`'s snap teardown). Built once
  // per install from the full bindings object (future pick-loop slices add
  // fields to `StageInteractionBindings`, not a narrower parallel type).
  // `onCanvasPoint` / `onCanvasHover` / `activeSpaceId` / `identityFallbackSpaceId`
  // / `osnapBindingsRef` / `draftActiveRef` / `orthoBasePointRef` now live ONLY
  // inside the pick loop — this file has zero direct reads/writes of them.
  const pickLoop = createInteractivePickLoop(bindings);

  let panState: PanDragState | null = null;
  let leftDragState: LeftDragState | null = null;
  // cmd-pan — active LEFT-button drag of a realtime-pan session. Captures the
  // paper-camera world centre at pointerdown so each move recomputes from the
  // TOTAL pixel delta (drift-free, like MMB `panState`). P2-4 — `vpRedirect`
  // mirrors the MMB path (drag over an unlocked active VP pans its model view).
  let lmbPanState:
    | {
        startX: number;
        startY: number;
        camStartX: number;
        camStartY: number;
        vpRedirect: PanDragState["vpRedirect"];
        // cmd-pan P2-3 — PRE-DRAG view captured at drag START (pixels-per-world
        // zoom; panByPixels keeps zoom fixed). Pushed onto ZOOM-Previous only at a
        // confirmed non-zero completion. Null for a VP-redirected drag.
        preDragView: { centerX: number; centerY: number; zoom: number } | null;
      }
    | null = null;
  // CC-1c — captured paper-camera world centre at the START of a two-finger
  // touch pan, so the gesture-end can emit a single cumulative `pan` view-op
  // (the per-move touch path feeds incremental deltas and has no single
  // camStart like the MMB `panState`). Null when no touch pan is in flight.
  let touchPanStartCam: { x: number; y: number } | null = null;
  // #354 @codex — set when a `pointercancel` aborts a multi-touch gesture while a
  // finger still remains. The surviving finger(s)' later `pointerup` must NOT be
  // reinterpreted as a pan completion, a 2-finger-tap Escape, or a single-finger
  // tap / double-tap zoom — the OS reclaimed the gesture. Holds until ALL touches
  // lift, then clears so the next gesture starts clean.
  let touchGestureAborted = false;
  // B10 — touch gesture state. Lives outside the React tree because the
  // `pointer*` listeners install once and outlive entity-feed renders.
  const touchState: TouchState = createTouchState();

  function nowMs(): number {
    return typeof performance !== "undefined" && performance.now
      ? performance.now()
      : Date.now();
  }

  function clearLongPressTimer(): void {
    if (touchState.longPressTimer != null) {
      clearTimeout(touchState.longPressTimer);
      touchState.longPressTimer = null;
    }
  }

  function getOrtho(): THREE.OrthographicCamera | null {
    const cam = cameraRef.current as THREE.OrthographicCamera;
    return cam.isOrthographicCamera ? cam : null;
  }

  function relativeCursorClient(
    e: PointerEvent,
  ): { x: number; y: number } | null {
    const rect = overlayContainer?.getBoundingClientRect();
    if (!rect) return null;
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  /**
   * MC4-interactive Slice 3 — resolve whether a wheel/pan at `(clientX,
   * clientY)` must be redirected into the active VP's model view.
   *
   * Returns the active VP (incl. its `id`), the model point currently under
   * the cursor, and the VP's on-screen pixel height (for the pan factor) when
   * ALL redirect conditions hold; otherwise null (gesture → main camera).
   *
   * In MSPACE_VIA_VP the live (main) camera IS the paper camera (no dive on
   * enter — S2 in-place design), so `clientToWorld(..., ortho)` yields the
   * paper-world point. The gate + paper→model transform are delegated to the
   * shared `resolveActiveInputSpace` resolver; this wrapper adapts its
   * `ActiveInputSpace` result back into the legacy `{ viewport, cursorModel,
   * vpScreenHeightPx }` shape the wheel/pan callers expect.
   */
  function resolveViewportRedirect(
    clientX: number,
    clientY: number,
    ortho: THREE.OrthographicCamera,
  ): {
    viewport: ViewportLike & { id: string };
    cursorModel: { x: number; y: number };
    vpScreenHeightPx: number;
  } | null {
    const ctx = viewportRedirectRef?.current ?? null;
    // S2-S4 wire other pointer paths through `resolveActiveInputSpace`; the
    // wheel/pan path additionally requires `onViewportSetView` to be bound.
    if (!ctx || !onViewportSetView) return null;

    // Paper-world point under the cursor (main camera == paper camera here).
    const paperPt = clientToWorld(clientX, clientY, canvas, ortho);
    // VP on-screen pixel height = paper VP height × pixels-per-paper-unit.
    // For the manual OrthographicCamera, pixelsPerWorldY = zoom × canvasH /
    // (ortho.top − ortho.bottom) (the inverse of panByPixels' worldPerPxY).
    const frustumH = Math.max(ortho.top - ortho.bottom, 1e-12);
    const canvasH = Math.max(sizeRef.current.height, 1);
    const pixelsPerWorldY =
      (Math.max(ortho.zoom, MIN_ZOOM) * canvasH) / frustumH;

    // Single-source the gate + paper↔model transform through the shared
    // resolver (CLAUDE.md PRIORITY #2 — no duplicated redirect logic). The
    // identity result (viewport == null) maps 1:1 onto the legacy `null` return.
    const space = resolveActiveInputSpace({
      paperPt,
      paperPixelsPerWorld: pixelsPerWorldY,
      redirect: ctx,
      fallbackSpaceId: "paper",
    });
    const vp = space.viewport;
    if (!vp) return null;

    const cursorModel = space.toSpace(paperPt);
    const vpScreenHeightPx = Math.max(vp.height * pixelsPerWorldY, 1e-9);
    return {
      viewport: vp,
      cursorModel,
      vpScreenHeightPx,
    };
  }

  function onWheel(e: WheelEvent): void {
    const ortho = getOrtho();
    if (!ortho) return;
    e.preventDefault();
    const factor = wheelDeltaToZoomFactor(e.deltaY);

    // ── MC4-interactive Slice 3 — redirect zoom into the active VP ─────
    // Unlocked active VP + cursor-over-VP in MSPACE_VIA_VP → zoom the VP's
    // model view (viewCenter/viewHeight) instead of the paper camera.
    const redirect = resolveViewportRedirect(e.clientX, e.clientY, ortho);
    if (redirect && onViewportSetView) {
      // FRAME DISCIPLINE. `zoomViewportAroundCursor` is frame-AGNOSTIC — it
      // only needs its centre and its cursor point in the SAME frame — and
      // `cursorModel` came out of `space.toSpace` (`paperToModel`), which
      // returns the **WCS** model point (`viewTarget + R(twist)·viewCenter`; see
      // `viewportTransform2d`'s frame contract). So the centre fed in must be
      // the WCS one too; passing the stored 12/22 pair would mix DCS with WCS
      // and bake `target·(1 − ratio)` into the result on every notch.
      //
      // The entity's `viewCenter` field stays DCS (the authored group codes,
      // what the Properties palette shows and what the DXF round-trip keeps),
      // so the WCS result is un-offset by the SAME one-truth pair before it is
      // dispatched. Both halves live in `drawing-model` — never re-derived
      // here (CLAUDE.md PRIORITY #1).
      const next = zoomViewportAroundCursor(
        viewportWcsViewCenter(redirect.viewport),
        redirect.viewport.viewHeight,
        redirect.cursorModel,
        factor,
      );
      onViewportSetView(redirect.viewport.id, {
        viewHeight: next.viewHeight,
        viewCenter: viewportStoredViewCenterFromWcs(
          redirect.viewport,
          next.viewCenter,
        ),
      });
      invalidate();
      return;
    }

    zoomAroundCursor(
      ortho,
      factor,
      { x: e.clientX, y: e.clientY },
      canvas,
    );
    invalidate();
    (window as unknown as { __wheelDebug?: unknown }).__wheelDebug = {
      zoom: ortho.zoom,
      pos: [ortho.position.x, ortho.position.y, ortho.position.z],
      frustum: { left: ortho.left, right: ortho.right, top: ortho.top, bottom: ortho.bottom },
      factor: wheelDeltaToZoomFactor(e.deltaY),
      ts: Date.now(),
    };
  }

  /**
   * cmd-pan P2-3/P2-4 — finalise an in-flight LMB realtime-pan drag: emit the one
   * gated `pan` view-op AND, on the same confirmed non-zero gate, push the
   * pre-drag view onto the ZOOM-Previous ring. A zero-net drag pushes nothing.
   * Shared by pointerup + Esc/Enter-mid-drag exits.
   */
  function finalizeLmbPanDrag(): void {
    if (!lmbPanState) return;
    // cmd-pan r6 P2-2 — the pan drag is ending; the camera (main OR redirected
    // VP) has moved, so the cached snap from the last pre-pan move is stale.
    // Clear it here (shared by pointerup + Esc/Enter-mid-drag exits, and covers
    // the vpRedirect branch which returns below) so the next click after PAN
    // exit recomputes the snap under the new camera. Move-time clears already
    // ran per drag-move, but a pan whose only motion was the final move (or a
    // redirected-VP pan) is covered here too.
    pickLoop.invalidateSnapCacheForPan();
    if (lmbPanState.vpRedirect) return;
    const ortho = getOrtho();
    if (!ortho) return;
    const moved = emitPanComplete(
      { x: lmbPanState.camStartX, y: lmbPanState.camStartY },
      { x: ortho.position.x, y: ortho.position.y },
    );
    if (moved && lmbPanState.preDragView) {
      onPanSnapshotView?.(lmbPanState.preDragView);
    }
  }

  /**
   * cmd-pan P2-1/P2-4 — END a realtime-pan session WITHOUT raising a global Escape
   * (fire `onPanSessionEnd`, NOT `emitEscape`/`onTouchEscape` — that synthetic
   * Escape clears the selection/draft). P2-4 — finalise an in-flight LMB drag
   * (gated view-op + pre-drag history push) BEFORE dropping `lmbPanState`.
   * Returns true iff a session was ended.
   */
  function endPanInteraction(): boolean {
    if (panSessionActiveRef?.current !== true) return false;
    // P2-4 — flush an in-flight LMB pan drag before dropping it.
    finalizeLmbPanDrag();
    lmbPanState = null;
    onPanSessionEnd?.();
    return true;
  }

  /**
   * AutoCAD touch parity (B10) — extracts an Esc-equivalent cancel
   * helper that mirrors the native Escape-key path so 2-finger taps
   * and gesture-bail paths funnel through one place.
   */
  function emitEscape(): void {
    const picking = zoomPickingRef?.current;
    if (picking) picking.onCancel();
    if (leftDragState) {
      leftDragState = null;
      setOverlayState((prev) => ({
        ...prev,
        selectionWindow: null,
      }));
    }
    // cmd-pan P2-1 — a 2-finger tap (any emitEscape caller) that EXITS a pan ends
    // ONLY the pan (no global Escape → selection/draft preserved, as Esc/Enter).
    // No pan active → fall through to the global escape (rubber-band / zoom-pick).
    if (endPanInteraction()) return;
    onTouchEscape?.();
  }

  /**
   * CC-1c — emit a single `pan` view-op for a completed MAIN-camera pan gesture.
   * `start`/`end` are the paper-camera world centres at gesture start/end; the
   * cumulative displacement is their difference. A zero-displacement gesture (MMB
   * click that never moved, or a touch pan that didn't shift the centre) records
   * NOTHING — a "pan" view-op there would be a false success (#269). Shared by the
   * MMB-up + two-finger-touch-up paths so gating + delta math never drift. cmd-pan
   * P2-3 — returns whether the pan MOVED (non-zero net delta), computed
   * INDEPENDENTLY of `onPanComplete` being wired, so the LMB realtime-pan history
   * snapshot pushes on the SAME gate even in a telemetry-less harness.
   */
  function emitPanComplete(
    start: { x: number; y: number },
    end: { x: number; y: number },
  ): boolean {
    const deltaX = end.x - start.x;
    const deltaY = end.y - start.y;
    if (deltaX === 0 && deltaY === 0) return false;
    onPanComplete?.({ deltaX, deltaY });
    return true;
  }

  function onTouchPointerDown(e: PointerEvent): void {
    e.preventDefault();
    // #354 @codex (:1740) — while a canceled gesture is still being scrubbed (a
    // finger remained after pointercancel), IGNORE new fingers: don't track them
    // or let them recreate pinch/pan, so an OS-reclaimed gesture cannot mutate
    // the view before all touches lift. The flag clears when the survivors lift.
    if (touchGestureAborted) return;
    // Capture the pointer so subsequent move/up events fire on this
    // canvas even if the finger drifts onto a sibling overlay.
    try {
      canvas.setPointerCapture(e.pointerId);
    } catch {
      /* jsdom may not implement setPointerCapture */
    }

    const point = {
      pointerId: e.pointerId,
      startClient: { x: e.clientX, y: e.clientY },
      lastClient: { x: e.clientX, y: e.clientY },
      startedAtMs: nowMs(),
    };
    touchState.points.set(e.pointerId, point);

    const count = touchState.points.size;

    if (count === 1) {
      // Arm a long-press timer — fires `onContextMenu` (right-click /
      // shortcut menu) if the finger stays within the tap-tolerance for
      // LONG_PRESS_MS. The timer is cleared on the next move past the
      // threshold, on pointerup, or on a second finger.
      clearLongPressTimer();
      const startClient = point.startClient;
      const longPressFn = (): void => {
        touchState.longPressTimer = null;
        // Make sure no other gesture has displaced this tap.
        if (touchState.points.size !== 1) return;
        const p = touchState.points.get(e.pointerId);
        if (!p) return;
        if (
          touchDistance(p.startClient, p.lastClient) > TAP_MOVE_THRESHOLD_PX
        ) {
          return;
        }
        // Right-click / shortcut menu emit. We mirror the desktop
        // contextmenu wiring so the parent can show the same menu. No shift
        // concept on touch — the Shift+right-click Object Snap override menu
        // (W2.5) is a desktop-only invocation surface.
        if (onContextMenuCb) {
          onContextMenuCb({ x: startClient.x, y: startClient.y }, false);
        } else if (onRightClickEnter) {
          onRightClickEnter();
        }
      };
      touchState.longPressTimer = setTimeout(
        longPressFn,
        LONG_PRESS_MS,
      ) as unknown as number;
      // 1-finger touch in idle state behaves like an LMB-down for the
      // selection / pick-point branch — defer to leftDragState so a
      // subsequent move past the drag threshold opens a rubber band.
      const picking = zoomPickingRef?.current;
      if (!picking) {
        leftDragState = {
          startClientX: e.clientX,
          startClientY: e.clientY,
        };
      }
      return;
    }

    if (count === 2) {
      // Second finger lands → cancel any long-press and switch into a
      // 2-finger pinch / pan baseline.
      clearLongPressTimer();
      // A 2-finger gesture supersedes a 1-finger tap-or-drag. Drop the
      // half-formed left-drag so we don't accidentally finalise it on
      // pointerup. Also clear any rubber band that might have appeared
      // during the brief 1-finger phase.
      if (leftDragState) {
        leftDragState = null;
        setOverlayState((prev) =>
          prev.selectionWindow == null ? prev : { ...prev, selectionWindow: null },
        );
      }
      const fingers = Array.from(touchState.points.values());
      // Defensive: should always have ≥2 fingers when count===2, but
      // strict typing forces an explicit guard so we never read
      // `undefined.lastClient`.
      if (fingers.length < 2) return;
      const a = fingers[0]!.lastClient;
      const b = fingers[1]!.lastClient;
      const dist = touchDistance(a, b);
      const mid = midpoint(a, b);
      touchState.pinch = {
        initialDistancePx: dist,
        initialMidpointClient: mid,
        lastDistancePx: dist,
        lastMidpointClient: mid,
      };
      touchState.twoFingerMode = null;
      return;
    }

    // 3+ fingers — gesture deferred (mobile undo/redo per Autodesk
    // touch KB). We still clear timers so a stray 3rd finger does not
    // leave a long-press armed.
    clearLongPressTimer();
  }

  function onTouchPointerMove(e: PointerEvent): void {
    // #354 @codex (:1740) — an aborted (OS-reclaimed) gesture must not pan/zoom
    // the camera while its touches are still being scrubbed, including survivor
    // moves; ignore all touch moves until the abort flag clears (all touches up).
    if (touchGestureAborted) return;
    const ortho = getOrtho();
    if (!ortho) return;
    const tracked = touchState.points.get(e.pointerId);
    if (!tracked) return;
    tracked.lastClient = { x: e.clientX, y: e.clientY };

    const count = touchState.points.size;
    // Single-finger drag — pan the camera (no command active path) OR
    // promote the half-formed leftDragState into a rubber band the same
    // way the desktop LMB drag does. We always pan (matches AutoCAD's
    // documented "1-finger drag = pan" when no command is active); the
    // selection-window rubber band still fires too because the LMB drag
    // path is what `SceneContent.onPointerUp` consumes for window-pick.
    if (count === 1) {
      // Cancel long-press if the finger has moved past tolerance.
      if (
        touchDistance(tracked.startClient, tracked.lastClient) >
        TAP_MOVE_THRESHOLD_PX
      ) {
        clearLongPressTimer();
      }
      return;
    }

    if (count >= 2 && touchState.pinch) {
      const fingers = Array.from(touchState.points.values()).slice(0, 2);
      if (fingers.length < 2) return;
      const a = fingers[0]!.lastClient;
      const b = fingers[1]!.lastClient;
      const newDist = touchDistance(a, b);
      const newMid = midpoint(a, b);

      if (touchState.twoFingerMode == null) {
        const decided = decideTwoFingerMode(
          touchState.pinch.initialDistancePx,
          touchState.pinch.initialMidpointClient,
          newDist,
          newMid,
        );
        if (decided != null) {
          touchState.twoFingerMode = decided;
          // CC-1c — a two-finger pan just engaged; remember the paper-camera
          // world centre so the gesture-end can emit one cumulative `pan`
          // view-op (the per-move path below feeds incremental deltas).
          if (decided === "pan") {
            touchPanStartCam = { x: ortho.position.x, y: ortho.position.y };
            // cmd-pan r7 P2-2 — like the MMB/LMB direct pans, the two-finger
            // touch pan now OWNS the camera: cancel any in-flight tween so
            // CameraApiBinder stops re-applying its stale target (snap-back).
            // AFTER capturing `touchPanStartCam` so the start pose stays live.
            onCancelPendingTween?.();
          }
        }
      }

      if (touchState.twoFingerMode === "pinch") {
        // Apply the *incremental* zoom factor (current vs last distance)
        // so accumulated rounding error stays bounded across the gesture.
        const factor = pinchZoomFactor(
          touchState.pinch.lastDistancePx,
          newDist,
        );
        zoomAroundCursor(ortho, factor, newMid, canvas);
        invalidate();
      } else if (touchState.twoFingerMode === "pan") {
        const dx = newMid.x - touchState.pinch.lastMidpointClient.x;
        const dy = newMid.y - touchState.pinch.lastMidpointClient.y;
        const startCenter = { x: ortho.position.x, y: ortho.position.y };
        // panByPixels uses absolute deltas from a starting centre — feed
        // the incremental delta as if startCenter was the current pose.
        panByPixels(ortho, sizeRef.current, dx, dy, startCenter);
        invalidate();
      }

      touchState.pinch.lastDistancePx = newDist;
      touchState.pinch.lastMidpointClient = newMid;
    }
  }

  function onTouchPointerUp(e: PointerEvent): void {
    const tracked = touchState.points.get(e.pointerId);
    if (!tracked) return;
    touchState.points.delete(e.pointerId);
    try {
      canvas.releasePointerCapture(e.pointerId);
    } catch {
      /* jsdom may not implement releasePointerCapture */
    }
    clearLongPressTimer();
    const remaining = touchState.points.size;

    // #354 @codex — a prior pointercancel aborted this multi-touch gesture while
    // a finger remained. Suppress ALL gesture-end semantics for the survivor's
    // lift (pan-op emit, 2-finger-tap Escape, single-finger tap / double-tap
    // zoom) — the OS canceled it. Clear the marker + residual state once every
    // touch has lifted so the next gesture starts clean.
    if (touchGestureAborted) {
      touchPanStartCam = null;
      if (remaining === 0) {
        touchGestureAborted = false;
        touchState.pinch = null;
        touchState.twoFingerMode = null;
        // #354 @codex (:983) — also clear the saved single-tap state so a clean
        // tap AFTER the canceled gesture cannot pair with a PRE-cancel tap and
        // fire a spurious double-tap zoom; the canceled gesture starts clean.
        touchState.lastTapAtMs = 0;
        touchState.lastTapClient = null;
        if (leftDragState) {
          leftDragState = null;
          setOverlayState((prev) =>
            prev.selectionWindow == null
              ? prev
              : { ...prev, selectionWindow: null },
          );
        }
      }
      return;
    }

    // CC-1c — a two-finger touch pan ends the moment the gesture drops below two
    // fingers. Emit one cumulative `pan` view-op for the main-camera shift since
    // the pan engaged (touchPanStartCam), then clear it so a subsequent gesture
    // starts fresh. `emitPanComplete` no-ops on a zero displacement.
    if (touchPanStartCam && touchState.twoFingerMode === "pan" && remaining < 2) {
      const ortho = getOrtho();
      if (ortho) {
        emitPanComplete(touchPanStartCam, {
          x: ortho.position.x,
          y: ortho.position.y,
        });
      }
      touchPanStartCam = null;
    }

    // Track gesture-end for tap classification. A tap is "lifted before
    // the long-press timer fires AND moved less than the tolerance".
    const isTap =
      touchDistance(tracked.startClient, tracked.lastClient) <=
      TAP_MOVE_THRESHOLD_PX;

    // Two-finger tap → Esc semantics. Detect by examining the OTHER
    // finger when this is the first lift of a 2-finger gesture that
    // never resolved into pinch / pan (i.e. `twoFingerMode == null`).
    if (
      remaining === 1 &&
      touchState.pinch &&
      touchState.twoFingerMode == null
    ) {
      // We are mid-2-finger gesture; the user just lifted one finger
      // without ever crossing the pinch / pan disambiguation threshold.
      // AutoCAD touch KB: this is a 2-finger tap → Escape.
      emitEscape();
      // Clear the pinch baseline; the surviving finger is now an
      // ordinary 1-finger touch (but with a pre-elapsed startedAtMs, so
      // it can't trigger a fresh tap when lifted).
      touchState.pinch = null;
      touchState.twoFingerMode = null;
      return;
    }

    if (remaining === 0) {
      // Last finger lifted. Two cases:
      //   1. Just ended a 2-finger gesture → reset baselines.
      //   2. Single-finger tap → check for double-tap (zoom extents).
      const wasPinch = touchState.pinch != null;
      touchState.pinch = null;
      touchState.twoFingerMode = null;
      if (!wasPinch && isTap) {
        const upAt = nowMs();
        if (
          isDoubleTap(
            touchState.lastTapAtMs,
            touchState.lastTapClient,
            upAt,
            tracked.startClient,
          )
        ) {
          // Double-tap → ZOOM EXTENTS.
          touchState.lastTapAtMs = 0;
          touchState.lastTapClient = null;
          onTouchZoomExtents?.();
          return;
        }
        touchState.lastTapAtMs = upAt;
        touchState.lastTapClient = tracked.startClient;
      }
    }

    // Mirror the desktop LMB-up path: clear any half-formed left-drag /
    // selection window so a deferred tap doesn't leave the rubber band
    // visible after a touch-up.
    if (leftDragState && remaining === 0) {
      leftDragState = null;
      setOverlayState((prev) =>
        prev.selectionWindow == null
          ? prev
          : { ...prev, selectionWindow: null },
      );
    }
  }

  /**
   * MC4-interactive Slice 3 / cmd-pan P2-4 — resolve the per-VP pan redirect for
   * a pan that starts at (clientX, clientY). When the pointer is over an
   * UNLOCKED active VP in MSPACE_VIA_VP (and a viewport-set callback is wired),
   * capture the VP's view params so the WHOLE drag pans the VP's model view
   * (viewCenter) instead of the paper camera; `null` otherwise (paper-scoped
   * pan). Single-sourced so the MMB (`panState`) and the interactive LMB
   * (`lmbPanState`) pan paths build an IDENTICAL redirect descriptor.
   */
  function resolvePanVpRedirect(
    clientX: number,
    clientY: number,
    ortho: THREE.OrthographicCamera,
  ): PanDragState["vpRedirect"] {
    const redirect = resolveViewportRedirect(clientX, clientY, ortho);
    if (!redirect || !onViewportSetView) return null;
    return {
      viewportId: redirect.viewport.id,
      // WCS, not the stored DCS pair — see `PanDragState.vpRedirect`'s FRAME
      // note. `panViewportByPixels` rotates the pixel delta by `R(+twist)`,
      // which is the screen→WCS axis map, so its centre argument must be WCS.
      startWcsViewCenter: viewportWcsViewCenter(redirect.viewport),
      viewHeight: redirect.viewport.viewHeight,
      viewTarget: redirect.viewport.viewTarget,
      viewTwistAngle: redirect.viewport.viewTwistAngle,
      vpScreenHeightPx: redirect.vpScreenHeightPx,
    };
  }

  /**
   * Commit one frame of a REDIRECTED per-VP pan (MMB and cmd-pan LMB share
   * this; extracting it keeps the WCS↔DCS frame conversion in ONE place, which
   * is the whole point — a second copy is exactly how the two gestures would
   * drift apart again).
   *
   * `dxPx`/`dyPx` are the TOTAL pixel delta since pointerdown (drift-free
   * recompute from the captured start, like the main-camera pan).
   *
   * FRAME: `panViewportByPixels` maps the pixel delta onto MODEL (WCS) axes
   * via `R(+twist)`, so it is fed the captured **WCS** centre and its WCS
   * result is un-rotated + un-offset by `viewportStoredViewCenterFromWcs`
   * before it lands in the entity's DCS `viewCenter` field.
   */
  function applyVpRedirectPan(
    vr: NonNullable<PanDragState["vpRedirect"]>,
    dxPx: number,
    dyPx: number,
  ): void {
    if (!onViewportSetView) return;
    const nextWcsCenter = panViewportByPixels(
      vr.startWcsViewCenter,
      vr.viewHeight,
      dxPx,
      dyPx,
      vr.vpScreenHeightPx,
      vr.viewTwistAngle,
    );
    onViewportSetView(vr.viewportId, {
      viewCenter: viewportStoredViewCenterFromWcs(vr, nextWcsCenter),
    });
    invalidate();
  }

  function onPointerDown(e: PointerEvent): void {
    if (e.pointerType === "touch") {
      onTouchPointerDown(e);
      return;
    }
    if (e.button === 1) {
      // Middle button → pan. Prevent the browser's "auto-scroll"
      // gesture from taking over.
      e.preventDefault();
      const ortho = getOrtho();
      if (!ortho) return;
      // MC4-interactive Slice 3 — if the pan starts over an unlocked active VP
      // in MSPACE_VIA_VP, capture its view params so the whole drag pans the
      // VP's model view (viewCenter) instead of the paper camera.
      const vpRedirect = resolvePanVpRedirect(e.clientX, e.clientY, ortho);
      panState = {
        startX: e.clientX,
        startY: e.clientY,
        camStartX: ortho.position.x,
        camStartY: ortho.position.y,
        vpRedirect,
      };
      // cmd-pan r7 P2-2 — the MMB drag now OWNS the camera (mirrors the LMB
      // realtime-pan path): cancel any in-flight tween so CameraApiBinder stops
      // re-applying its stale target (e.g. starting an MMB pan right after a
      // `-PAN` displacement, before its ~300ms tween settles, would otherwise
      // snap back). AFTER `camStart`/`vpRedirect` are read above — cancel stops
      // only FUTURE frames, so the captured start pose stays the live one.
      onCancelPendingTween?.();
      try {
        canvas.setPointerCapture(e.pointerId);
      } catch {
        /* jsdom test env may not implement setPointerCapture */
      }
      return;
    }

    if (e.button === 0) {
      // cmd-pan — an active realtime-pan session owns the LMB: a left drag pans
      // the paper camera (like MMB); camStart captured once so the drag is drift-free.
      if (panSessionActiveRef?.current === true) {
        e.preventDefault();
        const ortho = getOrtho();
        if (!ortho) return;
        // cmd-pan P2-4 — honour the VP redirect like the MMB pan (LMB pan over an
        // unlocked active VP pans that VP's model view, not the paper sheet).
        const vpRedirect = resolvePanVpRedirect(e.clientX, e.clientY, ortho);
        lmbPanState = {
          startX: e.clientX,
          startY: e.clientY,
          camStartX: ortho.position.x,
          camStartY: ortho.position.y,
          vpRedirect,
          // P2-3 — capture (don't push) the PRE-DRAG view; pushed only at a
          // confirmed non-zero completion (VP-redirect → null). `zoom` is CSS
          // pixels-per-world (NOT `ortho.zoom`) — convert via the canonical helper,
          // else ZOOM Previous restores at the wrong scale when frustum≠canvas width.
          preDragView: vpRedirect
            ? null
            : {
                centerX: ortho.position.x,
                centerY: ortho.position.y,
                zoom: pixelsPerWorldFromOrtho(ortho, sizeRef.current),
              },
        };
        // cmd-pan r5 P2-3 — the drag now OWNS the camera: cancel any in-flight
        // tween so CameraApiBinder stops re-applying the stale target (snap-back).
        // AFTER the pre-drag base read above; cancel stops only FUTURE frames.
        onCancelPendingTween?.();
        try {
          canvas.setPointerCapture(e.pointerId);
        } catch {
          /* jsdom may not implement setPointerCapture */
        }
        return;
      }
      const picking = zoomPickingRef?.current;
      if (picking) {
        // Zoom picking diverts left-click to corner/entity picks; we
        // do NOT begin a selection drag.
        return;
      }
      leftDragState = {
        startClientX: e.clientX,
        startClientY: e.clientY,
      };
      return;
    }
  }

  function onPointerMove(e: PointerEvent): void {
    if (e.pointerType === "touch") {
      onTouchPointerMove(e);
      return;
    }
    const ortho = getOrtho();
    if (!ortho) return;

    // ── MMB pan branch (highest priority — runs even mid-zoom-pick) ─
    if (panState) {
      const dx = e.clientX - panState.startX;
      const dy = e.clientY - panState.startY;
      // MC4-interactive Slice 3 — redirected VP pan: move the active VP's
      // model viewCenter from the TOTAL pixel delta (drift-free, like the
      // main-camera pan which uses the captured camStart + total delta).
      if (panState.vpRedirect && onViewportSetView) {
        applyVpRedirectPan(panState.vpRedirect, dx, dy);
        return;
      }
      panByPixels(
        ortho,
        sizeRef.current,
        dx,
        dy,
        { x: panState.camStartX, y: panState.camStartY },
      );
      invalidate();
      return;
    }

    // ── cmd-pan LMB realtime-pan branch (interactive session) ───────
    // Same panByPixels engine as MMB; recomputes from the TOTAL delta (drift-free).
    if (lmbPanState) {
      const dx = e.clientX - lmbPanState.startX;
      const dy = e.clientY - lmbPanState.startY;
      // cmd-pan r6 P2-2 — the camera (main OR redirected VP) is about to move,
      // so the snap cached on the last pre-pan move is now stale. Clear it
      // BEFORE the early returns below so a post-pan click with no intervening
      // move recomputes the snap under the new camera (see
      // `pickLoop.invalidateSnapCacheForPan`).
      pickLoop.invalidateSnapCacheForPan();
      // cmd-pan P2-4 — redirected VP pan: move the active VP's model viewCenter.
      if (lmbPanState.vpRedirect && onViewportSetView) {
        applyVpRedirectPan(lmbPanState.vpRedirect, dx, dy);
        return;
      }
      // cmd-pan P2-3 — no history push here (deferred to finalizeLmbPanDrag's
      // confirmed non-zero gate, so a zero-NET drag pushes nothing).
      panByPixels(
        ortho,
        sizeRef.current,
        dx,
        dy,
        { x: lmbPanState.camStartX, y: lmbPanState.camStartY },
      );
      invalidate();
      return;
    }

    // ── Crosshair / overlay cursor tracking ─────────────────────
    const cursor = relativeCursorClient(e);
    if (cursor) {
      setOverlayState((prev) => ({
        ...prev,
        cursorClient: cursor,
        crosshairVisible: true,
      }));
    }
    // Mirror cursor to the ROLLOVERTIPS card's positioning store.
    onCursorClientChange?.(cursor);

    // ── OSNAP resolve + cache + hover/marker emit (pick-loop module) ───
    // `pickLoop.resolveAndCacheSnap` remembers the last cursor position
    // internally (absolute client coords, so a later OSNAP-bitmask change
    // with NO pointer move can re-run the exact same resolution via
    // `refreshSnapAtLastPointer`) before resolving. The snap-resolve-and-
    // cache work is identical whether it runs from a real pointer move OR
    // from that refresh path, so it lives in one place to keep both byte-
    // identical.
    pickLoop.resolveAndCacheSnap(e.clientX, e.clientY, ortho);

    // ── Zoom-rubber overlay update (during ZOOM Window picking) ─
    const picking = zoomPickingRef?.current;
    if (picking?.mode === "zoom-window" && picking.firstCorner && cursor) {
      const overlayRect = overlayContainer?.getBoundingClientRect();
      if (overlayRect) {
        // Project the firstCorner (world coords) to client coords for
        // the rubber-band rectangle.
        const ndcStart = new THREE.Vector3(
          picking.firstCorner.x,
          picking.firstCorner.y,
          0,
        ).project(ortho);
        const startClientX =
          ((ndcStart.x + 1) / 2) * Math.max(sizeRef.current.width, 1);
        const startClientY =
          ((-ndcStart.y + 1) / 2) * Math.max(sizeRef.current.height, 1);
        setOverlayState((prev) => ({
          ...prev,
          zoomRubber: {
            visible: true,
            startScreen: { x: startClientX, y: startClientY },
            currentScreen: cursor,
          },
        }));
      }
    }

    // ── LMB drag-rectangle update ──────────────────────────────
    if (leftDragState && cursor) {
      const dx = Math.abs(e.clientX - leftDragState.startClientX);
      const dy = Math.abs(e.clientY - leftDragState.startClientY);
      const distance = Math.max(dx, dy);
      if (distance > DRAG_THRESHOLD_PX) {
        const overlayRect = overlayContainer?.getBoundingClientRect();
        if (overlayRect) {
          const startScreen = {
            x: leftDragState.startClientX - overlayRect.left,
            y: leftDragState.startClientY - overlayRect.top,
          };
          setOverlayState((prev) => ({
            ...prev,
            selectionWindow: {
              visible: true,
              startScreen,
              currentScreen: cursor,
              mode:
                e.clientX >= leftDragState!.startClientX ? "window" : "crossing",
            },
          }));
        }
      }
    }
  }

  function onPointerUp(e: PointerEvent): void {
    if (e.pointerType === "touch") {
      onTouchPointerUp(e);
      return;
    }
    if (e.button === 1 && panState) {
      // CC-1c — the MMB pan gesture just ended. Emit one `pan` view-op for the
      // cumulative MAIN-camera displacement (start centre captured at
      // pointerdown vs. the live camera now). A REDIRECTED per-VP pan
      // (`vpRedirect`) moved the viewport's model view via onViewportSetView —
      // NOT the paper camera — and records its own audit, so it emits no
      // main-view pan view-op here.
      if (!panState.vpRedirect) {
        const ortho = getOrtho();
        if (ortho) {
          emitPanComplete(
            { x: panState.camStartX, y: panState.camStartY },
            { x: ortho.position.x, y: ortho.position.y },
          );
        }
      }
      panState = null;
      try {
        canvas.releasePointerCapture(e.pointerId);
      } catch {
        /* jsdom may not implement releasePointerCapture */
      }
      return;
    }

    // cmd-pan — a realtime-pan LMB drag just ended. Finalise it (gated view-op +
    // pre-drag ZOOM-Previous push, #R4/P2-3). The session stays active (realtime
    // PAN keeps panning until Esc/Enter); only the per-drag state is dropped here.
    if (e.button === 0 && lmbPanState) {
      finalizeLmbPanDrag();
      lmbPanState = null;
      try {
        canvas.releasePointerCapture(e.pointerId);
      } catch {
        /* jsdom may not implement releasePointerCapture */
      }
      return;
    }

    if (e.button === 0) {
      const picking = zoomPickingRef?.current;
      const ortho = getOrtho();
      if (picking && ortho) {
        const world = clientToWorld(e.clientX, e.clientY, canvas, ortho);
        if (picking.mode === "zoom-window") {
          if (!picking.firstCorner) {
            picking.onPickCorner1?.(world);
          } else {
            picking.onPickCorner2?.(world);
          }
        } else if (picking.mode === "zoom-object") {
          // Hit-test for the picked entity; bubble up the id (or
          // empty pick → cancel via Esc semantics handled by adapter).
          const idx = spatialIndexRef.current;
          const refinedPick = refinedEntityPickRef?.current ?? null;
          if (idx || refinedPick) {
            const tol =
              (ortho.right - ortho.left) /
              (Math.max(ortho.zoom, MIN_ZOOM) *
                Math.max(sizeRef.current.width, 1)) *
              DRAG_THRESHOLD_PX;
            // Codex PR #784 round 7 P2 — AutoCAD's `ZOOM Object` prompts
            // "Select objects", i.e. it picks by the SAME rules a plain click
            // does. Route through the host's REFINED picker
            // (`scene/refinedEntityPick.ts`, published by
            // `installStageClickHandlers`): locked / off / frozen + VPLAYER
            // gating, the cache-direct wrapper LIFT, and the child-distance
            // refinement. The bare `SpatialIndex.pick` below answers from the
            // smallest intersecting AABB alone, so with a Block's metadata
            // record now spanning its whole definition bounds, a click in the
            // EMPTY interior of a sparse INSERT zoomed to that INSERT while a
            // click on one of its strokes could miss it.
            //
            // Round 8 P2 — `world` is the MAIN-camera (paper) point and `tol`
            // its paper-space tolerance; the picker itself resolves the active
            // input space from them (paper→VP-model redirect, active-space
            // candidate gate, VPLAYER-frozen set of the active VP), exactly as
            // the click path does. Do NOT pre-transform them here — the main
            // camera never dives into a VP (`viewportContextController`).
            //
            // FALLBACK — `idx.pick` when the host published no picker (its
            // `.current` is null): a jsdom / unit caller, or a host that wires
            // `spatialIndexRef` only. Behaviour there is exactly as before —
            // bbox-only and WITHOUT those contextual gates, which is why every
            // real host (`Scene3DStage`) wires the picker.
            const hitId = refinedPick
              ? refinedPick(world, tol)
              : idx!.pick(world, tol);
            if (hitId !== null) {
              picking.onPickEntity?.(hitId);
            }
          }
        }
        return;
      }

      // ── LMB click → emit canvas point (drafting "specify point") ──
      // AutoCAD parity: drafting state-machine advances ONLY on a true
      // click, never on hover/move. Discriminate via `leftDragState` +
      // `DRAG_THRESHOLD_PX`: if the cursor stayed within the threshold
      // between pointerdown and pointerup, treat it as a click; if it
      // moved further, it was a drag (rubber-band selection window) and
      // we suppress the point emit. The commit logic itself (snap-cache
      // read/fallback resolve, and the W2.5 OSNAP-OVERRIDE-MODES-S2
      // extended-override operand capture / one-shot clear) lives in the
      // pick-loop module (`pickLoop.commitClick`).
      const wasClick =
        leftDragState !== null &&
        Math.max(
          Math.abs(e.clientX - leftDragState.startClientX),
          Math.abs(e.clientY - leftDragState.startClientY),
        ) <= DRAG_THRESHOLD_PX;

      if (wasClick) {
        pickLoop.commitClick(e.clientX, e.clientY);
      }

      // Always clear rubber band on pointerup; the actual selection
      // logic lives in `SceneContent.onPointerUp`, which runs in
      // parallel via its own listener (left-click pick + drag-window
      // selection finalisation).
      if (leftDragState) {
        leftDragState = null;
        setOverlayState((prev) => ({
          ...prev,
          selectionWindow: null,
        }));
      }
    }
  }

  function onContextMenu(e: MouseEvent): void {
    e.preventDefault();
    // AutoCAD SHORTCUTMENU parity:
    //   - `onContextMenu` (popup-menu) wins when the parent has bound it.
    //     The parent decides whether the active SHORTCUTMENU bit warrants
    //     a popup in the current mode; if not, it forwards to the
    //     RIGHTCLICKENTER fallback itself by calling
    //     `submitCommandTextRef.current("")`.
    //   - `onRightClickEnter` (legacy RIGHTCLICKENTER) is the fallback
    //     for callers that haven't migrated to SHORTCUTMENU semantics.
    if (onContextMenuCb) {
      // W2.5 — carry `shiftKey` so the parent can distinguish the AutoCAD
      // Shift+right-click Object Snap cursor menu from the normal
      // SHORTCUTMENU popup (`useStageContextMenu.handleStageContextMenu`).
      onContextMenuCb({ x: e.clientX, y: e.clientY }, e.shiftKey);
      return;
    }
    if (onRightClickEnter) onRightClickEnter();
  }

  function onPointerLeave(): void {
    setOverlayState((prev) => ({
      ...prev,
      cursorClient: null,
      crosshairVisible: false,
      // POLAR overlay follows the cursor — clear it when the cursor leaves.
      polarGuide: null,
      polarTip: null,
      // OTRACK-SUBSYSTEM (local Codex review round 4 P2) — the alignment-path
      // guide + its distance/angle tooltip are ALSO cursor-following (they
      // are projected FROM the acquired point(s) THROUGH the live cursor
      // position), so they must clear on the same signal as POLAR's. The
      // acquired-point "+" markers themselves are deliberately left alone —
      // unlike the guide/tip, a marker denotes a FIXED acquired point, not a
      // cursor-relative visual, and AutoCAD keeps acquired points armed
      // while the cursor is off-canvas (e.g. moved to the command line).
      otrackGuides: null,
      otrackTip: null,
    }));
    onCursorClientChange?.(null);
    // MC4 edge case #2 / snap-cache teardown — delegated to the pick-loop
    // module (`pickLoop.handlePointerLeave`), which owns the hover-clear
    // gating (a draft in progress must NOT clear the draft's hover point —
    // AutoCAD floating-MSPACE parity, see that module's doc) and resets the
    // snap cache + last-move-client.
    pickLoop.handlePointerLeave();
    // B12 — cursor left the canvas; drop any stacked-candidate state
    // so the badge/dialog don't linger on top of unrelated UI panels.
    const cyclingStore = useSelectionCyclingStore.getState();
    if (cyclingStore.stackedCandidates.length > 0) {
      cyclingStore.setStackedCandidates([]);
    }
  }

  /**
   * AutoCAD touch parity (B10) — `pointercancel` fires when the OS
   * reclaims a touch (e.g. user receives a system gesture or palm
   * rejection kicks in). We must scrub any in-flight gesture state so
   * the next touch starts from a clean baseline.
   */
  /**
   * cmd-pan r7 P2-3 — finalise/scrub an in-flight LMB realtime-pan drag when the
   * browser aborts the pointer sequence WITHOUT a later `pointerup` (lost pointer
   * capture, OS interruption, tab/window focus change). Without this, `lmbPanState`
   * would stay non-null and every subsequent `pointermove` would keep taking the
   * LMB-pan branch (stuck panning) even though the gesture is over. Mirrors the
   * `pointerup` finalisation: `finalizeLmbPanDrag()` records a real move (and emits
   * nothing for a zero-net drag — it is gated + idempotent), then drops the state.
   * Shared by non-touch `pointercancel` AND `lostpointercapture`.
   */
  function scrubLmbPanOnAbort(e: PointerEvent): void {
    if (!lmbPanState) return;
    finalizeLmbPanDrag();
    lmbPanState = null;
    try {
      canvas.releasePointerCapture(e.pointerId);
    } catch {
      /* already released / jsdom may not implement releasePointerCapture */
    }
  }

  function onPointerCancel(e: PointerEvent): void {
    if (e.pointerType !== "touch") {
      // cmd-pan r7 P2-3 — a mouse/pen pointer sequence was canceled (lost capture,
      // OS interruption). Finalise + drop any armed LMB realtime-pan drag so the
      // stage can't get stuck in the LMB-pan branch on the next move.
      scrubLmbPanOnAbort(e);
      return;
    }
    touchState.points.delete(e.pointerId);
    // #354 @codex — a canceled touch ABORTS the in-flight gesture. Scrub the
    // pan-start so a later pointerup emits no spurious `pan` view-op for a pan
    // the OS reclaimed (palm rejection / system gesture), AND scrub the
    // two-finger gesture state. A canceled pan logs nothing.
    touchPanStartCam = null;
    touchState.pinch = null;
    touchState.twoFingerMode = null;
    if (touchState.points.size === 0) {
      // Last finger canceled — nothing survives, reset cleanly.
      touchGestureAborted = false;
      clearLongPressTimer();
      if (leftDragState) {
        leftDragState = null;
        setOverlayState((prev) =>
          prev.selectionWindow == null
            ? prev
            : { ...prev, selectionWindow: null },
        );
      }
    } else {
      // A finger still remains — mark the gesture aborted so the survivor's
      // later pointerup is fully suppressed (no pan-emit, no 2-finger-tap
      // Escape, no single-finger tap / double-tap zoom) until all touches lift.
      touchGestureAborted = true;
    }
  }

  /**
   * cmd-pan r7 P2-3 — `lostpointercapture` fires when the (implicit) pointer
   * capture armed at LMB-pan start is lost without a `pointerup`: tab/window
   * focus change, OS gesture, or an explicit `releasePointerCapture` elsewhere.
   * Finalise + drop the armed LMB realtime-pan drag so a subsequent `pointermove`
   * does NOT keep taking the LMB-pan branch (stuck panning). Touch capture loss is
   * already covered by the `pointercancel` scrub above; this is the mouse/pen path.
   */
  function onLostPointerCapture(e: PointerEvent): void {
    if (e.pointerType === "touch") return;
    scrubLmbPanOnAbort(e);
  }

  /**
   * cmd-pan P2-1/P2-6 — exit an active realtime-pan session on Esc OR Enter
   * (AutoCAD: both end a realtime PAN) via the dedicated path (NOT emitEscape/
   * onTouchEscape — that synthetic Escape clears the selection/draft). Needs
   * `stopImmediatePropagation` (not just preventDefault — `useKeyboardShortcuts`
   * does not bail on a default-prevented key). Returns true iff it ended a session.
   */
  function tryExitPanOnKey(e: KeyboardEvent): boolean {
    const isExit = e.key === "Escape" || e.key === "Enter";
    if (panSessionActiveRef?.current !== true || !isExit) return false;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    endPanInteraction();
    return true;
  }

  /**
   * cmd-pan P2-1 — DEDICATED CAPTURE-PHASE window listener for the PAN-exit keys.
   * `Scene3DStage` re-adds its window keydown on space/layout change, so the
   * stage BUBBLE handler may register AFTER the global `useKeyboardShortcuts`
   * one; a bubble-phase `stopImmediatePropagation` only stops listeners added
   * AFTER itself, so the global handler would run first (Enter repeats, Esc
   * clears). A CAPTURE-phase listener always fires before ANY bubble listener
   * regardless of order, stopping the key before the global handler sees it.
   */
  function onKeyDownPanExitCapture(e: KeyboardEvent): void {
    // cmd-pan P2-1 — IGNORE the key in an editable control / dialog: bail (let it
    // bubble to the input's own onKeyDown) so `LINE`↵ in the command line submits
    // instead of only ending PAN; the App-level command-start teardown supersedes.
    if (isPanExitKeyGuarded(e.target, document.activeElement)) return;
    tryExitPanOnKey(e);
  }

  function onKeyDown(e: KeyboardEvent): void {
    // cmd-pan — fallback PAN-exit (capture-phase listener normally stops it first;
    // defense-in-depth, idempotent — no-ops once the flag clears). P2-1 — same
    // focus gate as capture: never end PAN from a key destined for an input/dialog.
    if (!isPanExitKeyGuarded(e.target, document.activeElement) && tryExitPanOnKey(e))
      return;
    if (e.key !== "Escape") return;
    const picking = zoomPickingRef?.current;
    if (picking) {
      picking.onCancel();
      e.preventDefault();
    }
    if (leftDragState) {
      leftDragState = null;
      setOverlayState((prev) => ({
        ...prev,
        selectionWindow: null,
      }));
    }
  }

  canvas.addEventListener("wheel", onWheel, { passive: false });
  canvas.addEventListener("pointerdown", onPointerDown);
  canvas.addEventListener("pointermove", onPointerMove);
  canvas.addEventListener("pointerup", onPointerUp);
  canvas.addEventListener("pointercancel", onPointerCancel);
  // cmd-pan r7 P2-3 — `lostpointercapture` fires when implicit/explicit pointer
  // capture is lost without a `pointerup` (tab/window focus change, OS gesture).
  // Scrub any armed LMB realtime-pan drag so it can't get stuck panning.
  canvas.addEventListener("lostpointercapture", onLostPointerCapture);
  canvas.addEventListener("pointerleave", onPointerLeave);
  canvas.addEventListener("contextmenu", onContextMenu);
  // cmd-pan P2-1 — capture-phase FIRST so PAN-exit beats the global bubble handler.
  window.addEventListener("keydown", onKeyDownPanExitCapture, { capture: true });
  window.addEventListener("keydown", onKeyDown);

  return {
    dispose: () => {
      clearLongPressTimer();
      // OTRACK-SUBSYSTEM — cancel any pending hover-dwell acquisition timer;
      // see `PickLoopHandle.dispose`'s doc for why this must run on every
      // teardown, not just `pointerleave`.
      pickLoop.dispose();
      canvas.removeEventListener("wheel", onWheel);
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointercancel", onPointerCancel);
      canvas.removeEventListener("lostpointercapture", onLostPointerCapture);
      canvas.removeEventListener("pointerleave", onPointerLeave);
      canvas.removeEventListener("contextmenu", onContextMenu);
      window.removeEventListener("keydown", onKeyDownPanExitCapture, {
        capture: true,
      });
      window.removeEventListener("keydown", onKeyDown);
    },
    refreshSnapAtLastPointer: pickLoop.refreshSnapAtLastPointer,
  };
}
